import {makeVar} from '@apollo/client';

import {BenchlingAppUiBlockType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {BenchlingAppFeature} from './features/types';
import * as uiBlock from './ui-blocks/UiBlockTypes';

export const CANVAS_POLLING_FREQUENCY_MS = 3000;

/**
 * Runs predicate() over every member of uiBlocks, recursively visiting any Section block children.
 *
 * The returned uiBlock tree will have the same structure in terms of Section and children as the original
 * parameter. This is useful when needing to transform every uiBlock.
 */
export function mapOverBlocks(
  uiBlocks: Array<uiBlock.UiBlock>,
  predicate: (block: uiBlock.UiBlock) => uiBlock.UiBlock
): Array<uiBlock.UiBlock> {
  return uiBlocks.map((block: uiBlock.UiBlock) => {
    if (block.type === BenchlingAppUiBlockType.SECTION) {
      return {
        ...block,
        children: mapOverBlocks(block.children, predicate),
      };
    }
    return predicate(block);
  });
}

/**
 * Runs predicate() over every member of uiBlocks, recusively visiting any Section block children.
 *
 * The array responses from predicate() are collected and flattened one level, similar to array.flatMap()
 * behavior.
 *
 * This is useful when needing to map over uiBlocks but the resultant is not necessarily a transformed
 * uiBlock (say, filtering to grab all TEXT_INPUT types in uiBlocks).
 */
export function flatMapOverBlocks<Type>(
  uiBlocks: Array<uiBlock.UiBlock>,
  predicate: (block: uiBlock.UiBlock) => Array<Type>
): Array<Type> {
  return uiBlocks.flatMap((block: uiBlock.UiBlock) => {
    if (block.type === BenchlingAppUiBlockType.SECTION) {
      return flatMapOverBlocks(block.children, predicate);
    }
    return predicate(block);
  });
}

export interface FeatureMap {
  [resourceId: string]: BenchlingAppFeature | null;
}

export const resourceIdToFeature = makeVar<FeatureMap>({});

export function resourceHasAssociatedFeature(resourceId: string, reactiveFeatureMap: FeatureMap): boolean {
  return reactiveFeatureMap.hasOwnProperty(resourceId) && reactiveFeatureMap[resourceId] != null;
}

export function setFeatureForResource(resourceId: string, feature: BenchlingAppFeature | null): void {
  const featureMapCopy = {...resourceIdToFeature()};
  featureMapCopy[resourceId] = feature;
  resourceIdToFeature(featureMapCopy);
}
